import { Stack } from '@ltvco/refresh-lib/theme';
import { MessageBoxContainer } from '@ltvco/refresh-lib/v2';
import { MessageContent } from '../PhotoDamageAnalyzer/MessageContent/MessageContent';
import { AssistantSection } from './AssistantSection';
import { baseMessageBoxContainerStyles } from './ChatPage/components/MessagesContainer';
import { useCallback } from 'react';
import type { Message } from '@ltvco/refresh-lib/v2';

interface MessagePairProps {
  userMessage: Message;
  assistantMessage: Message | null;
  userIndex: number;
  assistantIndex: number | null;
  isLastPair: boolean;
  isSticky: boolean;
  hasUserScrolled: boolean;
  loadingStatus: { message: string; subtitle: string };
  firstEstimateAssistantIndex: number;
  tailGap: number;
  messageRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  typingRef: React.MutableRefObject<HTMLDivElement | null>;
  gapRef: React.MutableRefObject<HTMLDivElement | null>;
}

/**
 * MessagePair - Renders a user message with optional assistant response
 * Helps reduce complexity in MessagesContainer's render method
 * @component
 */
export const MessagePair = ({
  userMessage,
  assistantMessage,
  userIndex,
  assistantIndex,
  isLastPair,
  isSticky,
  hasUserScrolled,
  loadingStatus,
  firstEstimateAssistantIndex,
  tailGap,
  messageRefs,
  typingRef,
  gapRef,
}: MessagePairProps) => {
  const shouldShowUserMessage = firstEstimateAssistantIndex !== -1;

  // Create ref callbacks internally to avoid creating new functions on each render
  const onUserMessageRef = useCallback(
    (el: HTMLDivElement | null) => {
      messageRefs.current[userIndex] = el;
    },
    [messageRefs, userIndex],
  );

  const onAssistantMessageRef = useCallback(
    (index: number, el: HTMLDivElement | null) => {
      messageRefs.current[index] = el;
    },
    [messageRefs],
  );

  const onTypingRef = useCallback(
    (el: HTMLDivElement | null) => {
      typingRef.current = el;
    },
    [typingRef],
  );

  const onGapRef = useCallback(
    (el: HTMLDivElement | null) => {
      gapRef.current = el;
    },
    [gapRef],
  );

  return (
    <Stack width="100%" alignItems="center" spacing={2}>
      {shouldShowUserMessage && (
        <MessageBoxContainer
          ref={onUserMessageRef}
          sx={baseMessageBoxContainerStyles}
        >
          <MessageContent
            content={userMessage.content}
            isHeader={true}
            isSticky={isSticky}
            hasUserScrolled={hasUserScrolled}
          />
        </MessageBoxContainer>
      )}
      <AssistantSection
        assistant={assistantMessage}
        assistantIndex={assistantIndex}
        isLastPair={isLastPair}
        hasUserScrolled={hasUserScrolled}
        loadingStatus={loadingStatus}
        firstAssistantIndexToSkip={firstEstimateAssistantIndex}
        onMessageRef={onAssistantMessageRef}
        onTypingRef={onTypingRef}
        onGapRef={onGapRef}
        tailGap={tailGap}
      />
    </Stack>
  );
};
